import { useMemo } from 'react'
import { RenderedCell } from '../widgets/RenderedCell'
import { Button } from '../widgets/Button'
import { ChangeSetView, groupChanges, setHeadline } from './ChangeSet'
import { TextDiffView, textDiffSummary } from './TextDiffView'
import { diffText, type TextDiff } from './textDiff'

/**
 * THE change card (owner 2026-10-21, "Poiesis-first"): one rendering of a set of writes — what will
 * change, on which records (by name, never an id), each attribute's old → new value, the records it
 * creates or archives, and who it affects — with its ACTIONS passed in by the place that shows it:
 *
 * - the Daimon chat's confirmation card: Apply / Dismiss (the person decides, monad/agent/confirmations.py);
 * - a pending proposed change (Work Inbox, the Approvals page): Approve / Decline.
 *
 * The shape is the server's card contract (monad/agent/ui_cards.json `confirmation`), built from tool
 * results, never from model text. A change that names its attribute kind renders both values through
 * the widget registry's read cells (RenderedCell); otherwise the server's words are shown.
 *
 * Two shapes read differently (SEN-460, one card per intent):
 * - a SET card (`set`: many records, one intent — "Category → Retail · 261 assets") groups identical
 *   changes into "261 × Category: (empty) → Retail" with the records paged on demand (ChangeSet.tsx);
 * - a LONG TEXT change (`display: 'long_text'`, e.g. a skill's instructions) is read as one text with the
 *   changes inline, by markdown section (TextDiffView.tsx).
 * The actions sit in a footer that sticks to the bottom of the scrolling view while the card is on it,
 * so Apply stays in reach of a long card on a phone.
 */
export interface ChangeCardChange {
  record: string
  field: string
  old?: string | null
  value?: string | null
  /** the attribute kind and config, when the server sends the raw values to render as read cells */
  kind?: string | null
  config?: Record<string, unknown> | null
  old_value?: unknown
  new_value?: unknown
  /** long_text: a long value (markdown or multi-line) shown as a word-level inline diff of old_value → new_value */
  display?: 'inline' | 'long_text' | null
}

/** A set card: one intent over many records (monad/agent/ui_cards.json `confirmation.set`). */
export interface ChangeCardSet {
  records: number
  fields: Array<{ field: string; count: number }>
  refused: Array<{ record: string; reason: string }>
}

export interface ChangeCardData {
  title: string
  description?: string | null
  values: Array<{ name: string; label: string; value: string }>
  changes: ChangeCardChange[]
  creates: number
  affected: Array<{ record_id: string; kenning: string }>
  on_apply: 'apply' | 'submit_for_review'
  status: string
  outcome?: { code?: string; message?: string } | null
  /** the server's one-line summary, e.g. "Category → Retail · 261 assets" */
  summary_line?: string | null
  set?: ChangeCardSet | null
}

export interface ChangeCardAction {
  key: string
  label: string
  variant?: 'primary' | 'quiet'
  onClick: () => void
  disabled?: boolean
  testId?: string
}

/** The state words of a card (the server's statuses). */
const STATE_WORDS: Record<string, string> = {
  pending: 'Waiting for you', applying: 'Applying…', applied: 'Applied', proposed: 'Sent for approval',
  refused: 'Not applied', failed: 'Not confirmed', dismissed: 'Dismissed', expired: 'Expired', undone: 'Undone',
}

export function changeStateWords(status: string): string {
  return STATE_WORDS[status] ?? status
}

function Value({ change, which, slug }: { change: ChangeCardChange; which: 'old' | 'new'; slug?: string }) {
  const raw = which === 'old' ? change.old_value : change.new_value
  const text = which === 'old' ? change.old : change.value
  if (change.kind && raw !== undefined && raw !== null) {
    // the read cell's own chrome (padding, one-line truncation) is a TABLE cell's: in a card the value
    // sits flush with the plain-text rows and wraps inside the card (owner, 2026-10-23)
    return <div className="min-w-0 [&_button]:whitespace-normal [&_button]:px-0 [&_button]:py-0 [&_button]:text-left [&_.truncate]:whitespace-normal [&_.truncate]:overflow-visible">
      <RenderedCell kind={change.kind} value={raw} config={change.config ?? undefined} slug={slug} />
    </div>
  }
  return <span>{text ?? '—'}</span>
}

const isLongText = (c: ChangeCardChange) => c.display === 'long_text'
const asText = (v: unknown, words: string | null | undefined) => (typeof v === 'string' ? v : words ?? '')

/** Without `set`, this many changes still read as a set (grouped), not as a table of rows. */
const SET_FROM = 12

/** Whether a card reads as a set: the server said so, or it changes many cells. */
export function isSetCard(card: Pick<ChangeCardData, 'set' | 'changes'>): boolean {
  return !!card.set || card.changes.filter((c) => !isLongText(c)).length >= SET_FROM
}

/** The diff of each long-text change, computed once per card (the header counts it, the view shows it). */
function useLongDiffs(changes: ChangeCardChange[]): Map<ChangeCardChange, TextDiff> {
  return useMemo(() => new Map(changes.filter(isLongText)
    .map((c) => [c, diffText(asText(c.old_value, c.old), asText(c.new_value, c.value))] as const)), [changes])
}

/** What the change does: its rows, creations and who it affects (no actions). */
export function ChangeSummary({ card, slug, diffs: given }: { card: ChangeCardData; slug?: string; diffs?: Map<ChangeCardChange, TextDiff> }) {
  const own = useLongDiffs(given ? [] : card.changes)
  const diffs = given ?? own
  const shownAsChanges = new Set(card.changes.map((c) => c.field))
  const values = card.values.filter((v) => !shownAsChanges.has(v.label))
  const long = card.changes.filter(isLongText)
  const rows = card.changes.filter((c) => !isLongText(c))
  const set = isSetCard(card)
  // a record being created has no current values: its rows are field -> value, without a "Now" column
  const hasOld = rows.some((c) => c.old !== undefined && c.old !== null)
  const records = new Set(card.changes.map((c) => c.record)).size
  return <div className="grid min-w-0 gap-2 text-sm" data-testid="change-summary">
    {set && rows.length > 0 && <ChangeSetView changes={rows} set={card.set}
      value={(c, which) => <Value change={c} which={which} slug={slug} />} />}
    {long.map((c, i) => <TextDiffView key={`${c.record}-${c.field}-${i}`} field={c.field}
      record={records > 1 ? c.record : null} diff={diffs.get(c)} showSummary={long.length > 1}
      oldText={asText(c.old_value, c.old)} newText={asText(c.new_value, c.value)} />)}
    {!set && rows.length > 0 && <table className="w-full table-fixed text-left" data-testid="change-rows">
      <thead className="text-xs text-muted-foreground">
        <tr><th className="w-28 pr-3 font-normal">Field</th>{hasOld && <th className="w-20 pr-3 font-normal">Now</th>}
          <th className="font-normal">{hasOld ? 'After' : 'Value'}</th></tr>
      </thead>
      <tbody>
        {rows.map((c, i) => <tr key={`${c.record}-${c.field}-${i}`} data-testid="change-row" className="align-top">
          <td className="pr-3"><span className="font-medium">{c.field}</span>
            {card.affected.length > 1 && <span className="block text-xs text-muted-foreground">{c.record}</span>}</td>
          {hasOld && <td className="min-w-0 break-words pr-3 text-muted-foreground" data-testid="change-old">
            {c.old === undefined ? '—' : <Value change={c} which="old" slug={slug} />}</td>}
          <td className="min-w-0 break-words" data-testid="change-new"><Value change={c} which="new" slug={slug} /></td>
        </tr>)}
      </tbody>
    </table>}
    {values.length > 0 && <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1" data-testid="change-values">
      {values.map((v) => <div key={v.name} className="contents">
        <dt className="text-muted-foreground">{v.label}</dt><dd className="break-words">{v.value}</dd>
      </div>)}
    </dl>}
    {card.creates > 0 && <p className="text-muted-foreground" data-testid="change-creates">
      Creates {card.creates === 1 ? 'a new record' : `${card.creates} new records`}</p>}
    {/* a set names its records in its groups; a short list says who it affects */}
    {!set && card.affected.length > 0 && <p className="text-muted-foreground" data-testid="change-affected">
      Affects: {card.affected.map((a) => a.kenning).join(', ')}</p>}
  </div>
}

/** The card: header (title, state), the summary, a note on what deciding does, and the actions. */
export function ChangeCard({ card, actions, slug, note, error, showState = true }: {
  card: ChangeCardData
  actions: ChangeCardAction[]
  slug?: string
  /** false where the surrounding item already says the state (an approval's header) */
  showState?: boolean
  /** what the primary action does, said before it is pressed (e.g. "Apply sends it to an admin") */
  note?: string | null
  error?: string | null
}) {
  const closed = card.status !== 'pending'
  const reason = (card.status === 'refused' || card.status === 'failed' || card.status === 'expired')
    ? card.outcome?.message : null
  const set = isSetCard(card)
  const long = card.changes.some(isLongText)
  const diffs = useLongDiffs(card.changes)
  // one long text: the header says how much of it changed ("2 of 12 sections changed · +25 / −3 words")
  const longOnly = diffs.size === 1 ? [...diffs.values()][0]! : null
  // the one line that says what the card does at a glance: the server's, else counted from a set's changes
  const summary = card.summary_line
    ?? (set ? setHeadline(groupChanges(card.changes.filter((c) => !isLongText(c))), card.set?.records
      ?? new Set(card.changes.map((c) => c.record)).size) : null)
  return <article data-testid="change-card" data-status={card.status} data-shape={long ? 'long_text' : set ? 'set' : 'rows'}
    className={`grid min-w-0 gap-3 rounded-float border border-border bg-card p-3 shadow-work ${long ? 'max-w-2xl' : 'max-w-xl'}`}>
    <header className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <h3 className="break-words font-semibold">{card.title}</h3>
        {summary && <p className="break-words text-sm text-foreground/80" data-testid="change-summary-line">{summary}</p>}
        {longOnly && <p className="text-xs text-muted-foreground" data-testid="change-text-stats">{textDiffSummary(longOnly)}</p>}
        {card.description && <p className="text-xs text-muted-foreground">{card.description}</p>}
      </div>
      {showState && <span data-testid="change-state" className="shrink-0 text-xs text-muted-foreground">{changeStateWords(card.status)}</span>}
    </header>
    <ChangeSummary card={card} slug={slug} diffs={diffs} />
    {!closed && note && <p className="text-xs text-muted-foreground" data-testid="change-note">{note}</p>}
    {reason && <p role="status" className="text-xs text-destructive" data-testid="change-reason">{reason}</p>}
    {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    {/* sticky: a long card keeps its actions at the bottom of the view while any of it is on screen */}
    {actions.length > 0 && <div data-testid="change-actions"
      className="sticky bottom-0 z-10 -mx-3 -mb-3 flex flex-wrap items-center gap-2 rounded-b-float border-t border-border bg-card px-3 py-2">
      {actions.map((a) => a.variant === 'primary'
        ? <Button key={a.key} variant="primary" size="sm" disabled={a.disabled}
          data-testid={a.testId ?? `change-action-${a.key}`} onClick={a.onClick}>{a.label}</Button>
        : <Button key={a.key} variant="quiet" disabled={a.disabled}
          data-testid={a.testId ?? `change-action-${a.key}`} onClick={a.onClick}>{a.label}</Button>)}
    </div>}
  </article>
}
